import moment from "moment";
import i18next from "i18next";
import { format } from "date-fns";
import { toast, Slide } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";
import { heicTo } from "heic-to";

import {
  ZERO,
  NUMBER,
  messages,
  timeZone,
  IS_ACTIVE,
  UNDEFINED,
  ALL_EVENT,
  ONE_THOUSAND,
  EMPTY_STRING,
  EVENT_TYPE_ID,
  sharedConstants,
  FILE_EXTENSIONS,
  localStorageKeys,
  galleryConstants,
  VIDEO_EXTENSIONS,
  MEDIA_MIME_TYPES,
  RECAP_FORM_FIELDS,
  customerConstants,
  DATE_PICKER_FORMAT,
  recapDetailsKeys,
  eventBookingStatus,
  DEFAULT_PHONE_COUNTRY,
  RECAP_FEEDBACK_ACTION,
  SHUTTERB_DETAILS_KEYS,
  eventBookingConstants,
  defaultEventSetupObject,
  DEFAULT_VALUE_CART_TABLE,
  CART_SUMMARY_STATUS,
  orderItemType,
  MIN_BOOKING_LEAD_DAYS,
} from "../constants";
import db from "../dexieDB";
import { getLocalStorageItem, SERVER_TRANSLATION_KEYS } from "../system";

export * from "./Utilities/eventBooking.utility";
export * from "./Utilities/html.utility";

export const updateObject = (oldObject, updatedProperties) => ({
  ...oldObject,
  ...updatedProperties,
});

export const customToast = (
  AllErrorMessages,
  type = toast.TYPE.INFO,
  position = toast.POSITION.TOP_RIGHT,
  time = 4000
) => {
  const options = {
    autoClose: time,
    type,
    hideProgressBar: true,
    position,
    pauseOnHover: true,
    transition: Slide,
    className: "primary",
  };
  if (Array.isArray(AllErrorMessages)) {
    AllErrorMessages.forEach((message) => {
      if (message) {
        toast(message, options);
      }
    });
  } else {
    toast(i18next.t(AllErrorMessages), options);
  }
};

export const customToastWithTextMsg = (
  AllErrorMessages,
  type = toast.TYPE.INFO,
  position = toast.POSITION.TOP_RIGHT,
  time = 4000
) => {
  const options = {
    autoClose: time,
    type,
    hideProgressBar: true,
    position,
    pauseOnHover: true,
    transition: Slide,
    className: "primary",
  };
  if (Array.isArray(AllErrorMessages)) {
    AllErrorMessages.forEach((message) => {
      if (SERVER_TRANSLATION_KEYS.includes(message)) {
        toast(i18next.t(message), options);
      }
    });
  } else {
    toast(AllErrorMessages, options);
  }
};

export const standardDateTimeFormat = (date) => {
  return moment(date).format("YYYY/MM/DD HH:mm");
};

export const formatTime = (epochTimestamp) => {
  const date = new Date(epochTimestamp * 1000);
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = weekdays[date.getDay()];
  const month = months[date.getMonth()];
  const dayOfMonth = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const timezoneOffset = -date.getTimezoneOffset() / 60;
  const timezoneSuffix =
    timezoneOffset >= 0 ? `+${timezoneOffset}` : timezoneOffset.toString();

  const formattedTime = `${day} ${month} ${dayOfMonth} ${year} ${hours}:${minutes}:${seconds} GMT${timezoneSuffix} (Pakistan Standard Time)`;
  return formattedTime;
};

// convert the epochSeconds to date format with time
// e.g: Sat Oct 25 2025 7:15:00 PM
export const formatTimeToTimeZone = (epochSeconds, ianaTimezone) => {
  if (typeof epochSeconds !== "number") return "";

  const date = new Date(epochSeconds * 1000);
  const tzOption = ianaTimezone || timeZone.UTC;

  const formatter = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: tzOption,
  });

  return formatter.format(date);
};

// get minimum allowed date
export const minimumAllowedDate = (numberOfFutureDays = 0) => {
  let currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + numberOfFutureDays);
  return currentDate.toISOString().split("T")[0];
};

// Converts epoch time in seconds to a formatted date string using DATE_PICKER_FORMAT.
// Returns fallback value if input is invalid or formatting fails.
export const formatEpochToDate = (epochSeconds, fallback = EMPTY_STRING) => {
  if (!epochSeconds) return fallback;
  try {
    return format(new Date(epochSeconds * ONE_THOUSAND), DATE_PICKER_FORMAT);
  } catch (error) {
    console.error("Error formatting epoch date:", error);
    return fallback;
  }
};

export const calculateTotalFileSizeInMbs = (filesArr) => {
  let tempArr = filesArr;
  if (!Array.isArray(filesArr)) {
    tempArr = [...Array.from(filesArr)];
  }
  return (
    tempArr.reduce((accomulator, currentValue) => {
      return accomulator + currentValue.size;
    }, 0) / sharedConstants.KB_TO_MB_BYTES
  );
};

export const convertBytesToMbs = (fileSize) => {
  let size = fileSize;
  if (fileSize instanceof File) {
    size = fileSize.size;
  }
  return size / sharedConstants.KB_TO_MB_BYTES;
};

export const convertDateToUTC = (epochSeconds, ianaTimezone) => {
  if (typeof epochSeconds !== NUMBER) return EMPTY_STRING;

  const date = new Date(epochSeconds * 1000);
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: ianaTimezone ? ianaTimezone : "UTC",
    timeZoneName: "short",
  };

  return date.toLocaleDateString("en-US", options).replace("at", "");
};

export const convertEpochTo12HourTime = (epochSeconds, ianaTimezone) => {
  if (typeof epochSeconds !== "number") return "";

  const date = new Date(epochSeconds * 1000);

  const options = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: ianaTimezone ? ianaTimezone : "UTC",
  };

  return date.toLocaleTimeString("en-US", options);
};

export const convertEpochToFormattedDate = (epochTimestamp) => {
  const getDate = new Date(epochTimestamp * 1000);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = months[getDate.getUTCMonth()];
  const day = getDate.getUTCDate();
  const year = getDate.getUTCFullYear();

  return `${month} ${day}, ${year}`;
};

export const convertLongDateToShortDate = (longDate) => {
  const date = new Date(longDate);
  const month = date.getMonth() + 1; // Adding 1 since months are 0-indexed
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const convertHeicToJpeg = async (file) => {
  const fileName = file.name.replace(
    /\.(heic|heif)$/i,
    `.${FILE_EXTENSIONS.JPEG}`
  );

  const jpegBlob = await heicTo({
    blob: file,
    type: MEDIA_MIME_TYPES.JPEG,
  });

  return new File([jpegBlob], fileName, { type: MEDIA_MIME_TYPES.JPEG });
};

export const ClearReelStorage = () => {
  localStorage.removeItem("activeTab");
  localStorage.removeItem("isOnlyReel");
  localStorage.removeItem("reelEventId");
  localStorage.removeItem("reelOrderId");
  localStorage.removeItem("NOT_NEW_EVENT");
  localStorage.removeItem("dataToSend");
  localStorage.removeItem("REEL_STORED");
  localStorage.removeItem("EVENT_DETAIL_STORED");
};

export const ClearEventStorage = (isTipOrExtend) => {
  localStorage.removeItem(localStorageKeys.ACTIVE_TAB);
  localStorage.removeItem(localStorageKeys.DATA_TO_SEND);
  localStorage.removeItem(localStorageKeys.ADD_RECAPZ);
  localStorage.removeItem(localStorageKeys.ADD_CANCELLED_RECAPZ);
  localStorage.removeItem(localStorageKeys.ADD_RECAPZ_ACTIVE_STEP);
  localStorage.removeItem(localStorageKeys.DRAFT_EVENTS);
  localStorage.removeItem(localStorageKeys.IS_DRAFT_EVENT);
  localStorage.removeItem(localStorageKeys.EDIT_EVENTS);
  localStorage.removeItem(localStorageKeys.IS_EDIT_EVENT);
  localStorage.removeItem(localStorageKeys.ADD_RECAPZ_BACKUP);
  localStorage.removeItem(localStorageKeys.TOTAL_EVENTS);
  localStorage.removeItem(localStorageKeys.BILLING_DETAILS);
  if (!isTipOrExtend) {
    localStorage.removeItem(customerConstants.NOT_NEW_EVENT);
    localStorage.removeItem(customerConstants.STORED_EVENT_DETAILS);
    localStorage.removeItem(customerConstants.CART_PREVIOUS_SCREEN);
    localStorage.removeItem(customerConstants.STORED_CUSTOMER_DASHBOARD_CART);
    localStorage.removeItem(localStorageKeys.EVENT_ALREADY_EXIST);
  }
};

export const ClearExtendOrTipStorage = () => {
  localStorage.removeItem("activeTab");
  localStorage.removeItem("STORED_EVENT_DETAILS");
  localStorage.removeItem("STORED_CUSTOMER_DASHBOARD_CART");
  localStorage.removeItem("CART_PREVIOUS_SCREEN");
  localStorage.removeItem("NOT_NEW_EVENT");
  localStorage.removeItem("dataToSend");
  localStorage.removeItem("EVENT_DETAIL_STORED");
};

export const isMediaTypeImage = (url) => {
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];

  const lowerUrl = url?.toLowerCase();
  const extension = lowerUrl?.substring(lowerUrl?.lastIndexOf("."));

  return imageExtensions.includes(extension);
};

export const getFutureDate = (daysToAdd) => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  return date;
};

export const isTimeInEpochSecond = (val) =>
  typeof val === NUMBER && !isNaN(val);

export const normalizeEventDates = (events) => {
  if (!Array.isArray(events)) return [];
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return events.map((item) => {
    if (item.isAlreadyConvertedToTimezone) {
      return {
        ...item,
        eventDate: isTimeInEpochSecond(item?.eventDate)
          ? formatTimeToTimeZone(item?.eventDate, timezone)
          : item?.eventDate || EMPTY_STRING,
        eventStartTime: isTimeInEpochSecond(item?.eventStartTime)
          ? formatTimeToTimeZone(item?.eventStartTime, timezone)
          : item?.eventStartTime || EMPTY_STRING,
        sbStartTime: isTimeInEpochSecond(item?.sbStartTime)
          ? formatTimeToTimeZone(item?.sbStartTime, timezone)
          : item?.sbStartTime || EMPTY_STRING,
      };
    }
    return {
      ...item,
      isAlreadyConvertedToTimezone: true,
      eventDate: isTimeInEpochSecond(item?.eventDate)
        ? item?.eventLocation?.timezone
          ? formatTimeToTimeZone(item?.eventDate, item?.eventLocation?.timezone)
          : formatEpochToDate(item.eventDate, EMPTY_STRING)
        : item?.eventDate || EMPTY_STRING,

      // when timezone is available then use the method formatTimeToTimeZone
      eventStartTime: isTimeInEpochSecond(item?.eventStartTime)
        ? item?.eventLocation?.timezone
          ? formatTimeToTimeZone(
              item?.eventStartTime,
              item?.eventLocation?.timezone
            )
          : formatTime(item.eventStartTime)
        : item?.eventStartTime || EMPTY_STRING,

      sbStartTime: isTimeInEpochSecond(item?.sbStartTime)
        ? item?.eventLocation?.timezone
          ? formatTimeToTimeZone(
              item?.sbStartTime,
              item?.eventLocation?.timezone
            )
          : formatTime(item.sbStartTime)
        : item?.sbStartTime || EMPTY_STRING,
    };
  });
};

export const getFormattedFormState = () => {
  const storedData = JSON.parse(
    localStorage.getItem(localStorageKeys.DATA_TO_SEND)
  );
  const events = storedData?.Event;

  if (!Array.isArray(events)) return [deepClone(defaultEventSetupObject)];

  const formState = normalizeEventDates(events)?.map((item) => ({
    ...item,
    id: item.id,
    eventName: item?.eventName || EMPTY_STRING,
    eventLocation: {
      ...item?.eventLocation,
      currentLocation: item?.eventLocation?.currentLocation || EMPTY_STRING,
      latitude: item?.eventLocation?.latitude || 0,
      longitude: item?.eventLocation?.longitude || 0,
      postalCode: item?.eventLocation?.postalCode || EMPTY_STRING,
    },
    apartment: item?.apartment || EMPTY_STRING,
    eventType: item?.eventType || EMPTY_STRING,
    additionalNotes: item?.additionalNotes || EMPTY_STRING,
    notes: item?.notes || EMPTY_STRING,
    duration: item?.duration || EMPTY_STRING,
    termsAndConditions: item?.termsAndConditions ?? false,
    privacyPolicy: item?.privacyPolicy ?? false,
    isGallerySharedPermission: item?.isGallerySharedPermission ?? true,
    isConsentForMarketing: item?.isConsentForMarketing ?? true,
    contactName: item?.contactName || EMPTY_STRING,
    phoneNumber: item?.phoneNumber || EMPTY_STRING,
  }));
  return formState;
};

// Regex for MM/DD/YYYY with 4-digit year only
export const isValidDate = (value) => {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  return regex.test(value);
};

export const combineDateAndTime = (date, time) => {
  if (!(date instanceof Date) || isNaN(date)) return EMPTY_STRING;
  if (!(time instanceof Date) || isNaN(time)) return EMPTY_STRING;

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds()
  );
};

/**
 * Formats a date string into a readable format like: "June 12, 2025 at 3:45 PM"
 *
 * @param {string} dateString - A valid date string (e.g., "2025-06-12T15:45:00Z")
 * @returns {string} - Formatted date string or "Invalid Date" if input is invalid
 *
 * Example:
 *   formatCustomDate("2025-06-12T15:45:00Z");
 *   // Output: "June 12, 2025 at 3:45 PM"
 */
export const formatCustomDate = (dateString) => {
  let date;

  // If input is a number, treat it as a Unix timestamp
  if (typeof dateString === "number") {
    date = new Date(dateString * 1000);
  } else {
    date = new Date(dateString);
  }

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const month = date.toLocaleString("en-US", { month: "long" });
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
};

// Truncate the string if it's longer than maxLength and add "..." at last
export const truncateString = (str, maxLength) => {
  if (typeof str !== "string") return EMPTY_STRING;
  return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
};

export const isPhoneValid = (value, required = false) => {
  if (!required) {
    if (!value || value.trim() === DEFAULT_PHONE_COUNTRY.COUNTRY) {
      return { isPhoneNumberValid: true, phoneValue: EMPTY_STRING };
    }
  }

  const phoneUtil = PhoneNumberUtil.getInstance();
  const stringValue = required ? String(value || EMPTY_STRING).trim() : value;
  const phoneValueWithPlus = stringValue.startsWith("+")
    ? stringValue
    : `+${stringValue}`;

  try {
    return {
      isPhoneNumberValid: phoneUtil.isValidNumber(
        phoneUtil.parseAndKeepRawInput(phoneValueWithPlus)
      ),
      phoneValue: phoneValueWithPlus,
    };
  } catch (error) {
    return { isPhoneNumberValid: false, phoneValue: phoneValueWithPlus };
  }
};

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const isHtmlContentEmpty = (html) => {
  if (!html) return true;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.innerText.trim() === "";
};

export const isAllRecapsValid = (addRecapz) => {
  for (let eventIndex = 0; eventIndex < addRecapz.length; eventIndex++) {
    const event = addRecapz[eventIndex];
    if (!Array.isArray(event.reelDetail) || event.reelDetail.length === 0) {
      return { eventIndex, recapIndex: 0 };
    }

    for (
      let recapIndex = 0;
      recapIndex < event.reelDetail.length;
      recapIndex++
    ) {
      const reelItem = event.reelDetail[recapIndex];
      const allFieldsValid =
        reelItem.description &&
        reelItem.music &&
        reelItem.orientation &&
        reelItem.duration &&
        reelItem.reelType &&
        reelItem.reelQuestionAnswer?.answer;

      const descriptionContentValid =
        reelItem.description && !isHtmlContentEmpty(reelItem.description);

      if (!allFieldsValid) {
        return { eventIndex, recapIndex };
      }

      if (!descriptionContentValid) {
        return {
          eventIndex,
          recapIndex,
          reason: RECAP_FORM_FIELDS.DESCRIPTION,
        };
      }
    }
  }

  return null;
};

export const getReOrderedRecapList = (updatedList, formState) => {
  const allEvent = updatedList.find((x) => x.eventId === ALL_EVENT.id);
  const others = formState
    .map((ev) => updatedList.find((x) => x.eventId === ev.id))
    .filter(Boolean);

  return allEvent ? [...others, allEvent] : others;
};

// Clear all entries from a specified Dexie table
// await clearDexieTable("logos");
export const clearDexieTable = async (tableName) => {
  try {
    if (db[tableName]) {
      await db[tableName].clear();
      console.log(`All data cleared from Dexie table: ${tableName}`);
    } else {
      console.warn(`Table "${tableName}" does not exist in Dexie.`);
    }
  } catch (error) {
    console.error(
      `Failed to clear data from Dexie table "${tableName}":`,
      error
    );
  }
};

/**
 * Converts time given in string into seconds
 * Time Format : 00:00:00 --> 0
 * @param {string '00:00:00'} timeString
 */
export const timeToSeconds = (timeString) => {
  const [minutes, seconds] = timeString.split(":").map(Number);
  return minutes * 60 + seconds;
};

export const secondsToTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const remainingSeconds = seconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const secs = Math.floor(remainingSeconds % 60);

  const format = (num) => num.toString().padStart(2, "0");

  return `${format(hours)}:${format(minutes)}:${format(secs)}`;
};

export const removeCommaAndSpaceFromLast = (str) => {
  if (typeof str === "string" && str.endsWith(", ")) {
    return str.slice(0, -2);
  }
  return str;
};

// Converts a date string like "Fri Jun 20 2025 17:0:0 GMT+5 (Pakistan Standard Time)"
//  into a formatted 12-hour time string like "5:00 PM".
export const formatDateIntoTime = (dateInput) => {
  try {
    if (!dateInput) return "";

    const date = new Date(dateInput);

    if (isNaN(date.getTime())) return "";

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Invalid date input:", error);
    return "";
  }
};

export const attachRecapToFormState = (
  formState,
  addRecapz,
  addRecapzBackup,
  recapTypes
) => {
  try {
    if (!Array.isArray(formState))
      throw new Error("formState must be an array");
    if (!Array.isArray(addRecapz))
      throw new Error("addRecapz must be an array");
    if (!Array.isArray(recapTypes))
      throw new Error("recapTypes must be an array");

    const updateFormState = formState.map((event) => {
      const backupRecap = addRecapzBackup?.find((r) => r.eventId === event.id);

      const oldTypeCounts = {};
      if (backupRecap?.reelDetail) {
        backupRecap.reelDetail.forEach((item) => {
          const key = item.reelMode;
          oldTypeCounts[key] = (oldTypeCounts[key] || 0) + 1;
        });
      }

      const matchedRecap = addRecapz.find(
        (recap) => recap.eventId === event.id
      );

      // Initialize recapTotals from recapTypes
      const recapTotals = recapTypes.reduce((acc, type) => {
        acc[type.name] = {
          reelMode: type.name,
          oldQuantity: oldTypeCounts[type.name] || 0,
          quantity: 0,
          amount: 0,
          price: type.price,
          status: eventBookingStatus.CREATED,
        };
        return acc;
      }, {});

      if (matchedRecap?.isChecked && Array.isArray(matchedRecap.reelDetail)) {
        const statusMap = {};
        const allAreCancel = matchedRecap.reelDetail.every(
          (recapItem) => recapItem.status === eventBookingStatus.CANCEL
        );

        const typeStats = {};

        // STEP 1: Build stats per type
        matchedRecap.reelDetail.forEach((item) => {
          const typeKey = item.reelMode;

          if (!typeStats[typeKey]) {
            typeStats[typeKey] = {
              total: 0,
              active: 0,
              canceled: 0,
              statuses: [],
              price: item.price,
            };
          }

          typeStats[typeKey].total++;
          typeStats[typeKey].statuses.push(item.status);

          if (item.status === eventBookingStatus.CANCEL) {
            typeStats[typeKey].canceled++;
          } else {
            typeStats[typeKey].active++;
          }
        });

        // Resolve statuses with priority: EDITED > CREATED > CANCEL
        Object.keys(typeStats).forEach((typeKey) => {
          const stats = typeStats[typeKey];
          const totalOld = oldTypeCounts[typeKey] || 0;

          if (!recapTotals[typeKey]) return;

          if (stats.active > 0) {
            // Some items still exist -> show remaining quantity
            recapTotals[typeKey].quantity = stats.active;
            recapTotals[typeKey].status = resolveStatus(stats.statuses);
          } else if (totalOld > 0) {
            // All removed -> mark as CANCEL instead of removing
            recapTotals[typeKey].quantity = totalOld;
            recapTotals[typeKey].status = eventBookingStatus.CANCEL;
          }

          // Price logic
          const effectivePrice = matchedRecap.isQuantityIncrease
            ? recapTotals[typeKey].price
            : stats.price;

          recapTotals[typeKey].price = effectivePrice;

          const isFullyCancelled =
            recapTotals[typeKey].quantity === 0 &&
            recapTotals[typeKey].oldQuantity > 0;

          if (isFullyCancelled) {
            // show refund based on OLD quantity
            recapTotals[typeKey].amount =
              recapTotals[typeKey].oldQuantity * effectivePrice;
          } else {
            // normal case
            recapTotals[typeKey].amount =
              recapTotals[typeKey].quantity * effectivePrice;
          }
        });
      }

      // Filter only types with quantity > 0
      const recapArray = Object.values(recapTotals).filter(
        (item) => item.quantity > 0 || item.status === eventBookingStatus.CANCEL
      );

      return {
        ...event,
        reels: recapArray,
      };
    });

    return updateFormState;
  } catch (error) {
    console.error("attach Recap To FormState error:", error);
    return formState || [];
  }
};

// Status priority helper
const resolveStatus = (statuses) => {
  if (!isArrayWithItems(statuses)) return null;

  if (statuses.includes(eventBookingStatus.EDITED))
    return eventBookingStatus.EDITED;
  if (statuses.includes(eventBookingStatus.CREATED))
    return eventBookingStatus.CREATED;
  return statuses[0];
};

// Merge ALL_EVENT recaps with backup
export const generateAllEventsRecap = (
  reels = [],
  recapTypes = [],
  addRecapzBackup = []
) => {
  const addRecapz = deepClone(reels);

  let allEvents = addRecapz.find((r) => r.eventId === ALL_EVENT.id);
  const allEventsBackup = addRecapzBackup?.find(
    (r) => r.eventId === ALL_EVENT.id
  );

  const oldTypeCounts = {};

  (allEventsBackup?.reelDetail || []).forEach((item) => {
    const key = item.reelMode;
    oldTypeCounts[key] = (oldTypeCounts[key] || 0) + 1;
  });

  if (
    allEvents &&
    (!isArrayWithItems(allEvents.reelDetail) ||
      allEvents.reelDetail.every(
        (recap) => recap.status === eventBookingStatus.CANCEL
      ))
  ) {
    allEvents = null;
  }

  // No ALL_EVENT in exist in both allEvents and allEventsBackup calculate directly
  if (!allEvents && !allEventsBackup) {
    return allEventRecapsCalculations(addRecapz, recapTypes, oldTypeCounts);
  }

  // Missing ALL_EVENT in addRecapz but present in allEventsBackup then copy allEventsBackup with status cancel
  if (!allEvents && allEventsBackup) {
    const canceledCopy = {
      ...allEventsBackup,
      reelDetail: allEventsBackup?.reelDetail.map((recap) => ({
        ...recap,
        status: eventBookingStatus.CANCEL,
      })),
      isQuantityIncrease: false,
    };

    // Pass directly without mutating addRecapz
    return allEventRecapsCalculations(
      [canceledCopy],
      recapTypes,
      oldTypeCounts
    );
  }

  // Present in both merge it
  if (allEvents && allEventsBackup) {
    let updatedReels = allEvents.reelDetail || [];
    const backupReels = allEventsBackup.reelDetail || [];

    // set isQuantityIncrease true/false
    allEvents.isQuantityIncrease =
      (allEvents.quantity ?? updatedReels.length) >
      (allEventsBackup.quantity ?? backupReels.length);

    if (backupReels.length >= updatedReels.length) {
      // Lookup for reel mode
      const backupReelsByMode = backupReels.reduce((acc, reel) => {
        acc[reel.reelMode] = reel;
        return acc;
      }, {});

      allEvents.reelDetail = backupReels.map((backupReel, index) => {
        const existing = updatedReels[index];

        if (existing) {
          const hasMatchingMode = backupReelsByMode[existing.reelMode];

          return {
            ...existing,
            id: backupReel.id,
            price:
              !allEvents.isQuantityIncrease && hasMatchingMode
                ? hasMatchingMode.price
                : existing.price,
            status:
              existing.status !== eventBookingStatus.CANCEL
                ? backupReel.status
                : existing.status,
          };
        }
        // If backup has more reels, mark extras as CANCEL
        return {
          ...deepClone(backupReel),
          status: eventBookingStatus.CANCEL,
        };
      });
    } else {
      // backupReels is smaller OR equal update id & status of first matching reels
      backupReels.forEach((backupReel, index) => {
        if (updatedReels[index]) {
          updatedReels[index].id = backupReel.id;

          // Check if this reelMode exists anywhere in backupReels
          const hasMatchingMode = backupReels.some(
            (backupReel) => backupReel.reelMode === updatedReels[index].reelMode
          );

          // Update price only if not increasing AND reelMode is present in backupReels
          if (!allEvents.isQuantityIncrease && hasMatchingMode) {
            updatedReels[index].price = backupReel.price;
          }

          if (updatedReels[index].status !== eventBookingStatus.CANCEL) {
            updatedReels[index].status = backupReel.status;
          }
        }
      });
    }
  }

  return allEventRecapsCalculations(addRecapz, recapTypes, oldTypeCounts);
};

// Calculate recap type, price, quantity, and total amount
export const allEventRecapsCalculations = (
  recapzArray,
  recapTypes,
  oldTypeCounts
) => {
  if (!isArrayWithItems(recapzArray) || !isArrayWithItems(recapTypes)) {
    return [];
  }

  // Only take All Event recap (eventId = -1)
  const allEventRecap = recapzArray.find(
    (recap) => recap.eventId === ALL_EVENT.id
  );
  if (!allEventRecap || !Array.isArray(allEventRecap.reelDetail)) return [];

  const recapSummary = {};
  const allCanceled = allEventRecap.reelDetail.every(
    (recapItem) => recapItem.status === eventBookingStatus.CANCEL
  );

  allEventRecap.reelDetail.forEach((recapItem) => {
    const { reelMode, status } = recapItem;
    if (!recapSummary[reelMode]) {
      recapSummary[reelMode] = {
        reelMode,
        oldQuantity: oldTypeCounts[reelMode] || 0,
        quantity: 0,
        amount: 0,
        price: 0,
        statuses: [],
        canceledCount: 0,
      };
    }

    if (status === eventBookingStatus.CANCEL) {
      recapSummary[reelMode].canceledCount++;
    } else {
      recapSummary[reelMode].quantity++;
    }

    // decide which recap price is used new/old
    const typePrice = recapTypes.find((r) => r.name === reelMode)?.price || 0;
    const effectivePrice = allEventRecap.isQuantityIncrease
      ? typePrice // use new recapTypes price
      : recapItem.price; // use old reel price

    recapSummary[reelMode].price = effectivePrice;
    recapSummary[reelMode].amount += effectivePrice;
    // if (status !== eventBookingStatus.CANCEL) {
    // }

    if (status) recapSummary[reelMode].statuses.push(status);
  });

  return Object.values(recapSummary).map((recap) => {
    const isFullyCancelled = recap.quantity === 0 && recap.canceledCount > 0;

    let finalQuantity = recap.quantity;
    let finalStatus = resolveStatus([...new Set(recap.statuses)]);

    // Handle full cancel case
    if (isFullyCancelled) {
      finalQuantity = recap.oldQuantity;
      finalStatus = eventBookingStatus.CANCEL;
    }

    const finalAmount = isFullyCancelled
      ? recap.oldQuantity * recap.price
      : finalQuantity * recap.price;

    return {
      reelMode: recap.reelMode,
      oldQuantity: recap.oldQuantity, // <-- IMPORTANT (you were missing this)
      quantity: finalQuantity,
      price: recap.price,
      amount: finalAmount,
      status: finalStatus,
    };
  });
};

// Calculate the sum of all shutterB and recap total fee
export const calculateShutterAndRecapTotalFee = (
  formState = [],
  isEditEvent
) => {
  return formState.reduce(
    (totals, event) => {
      const { duration = 0, orderItems = [], reels = [] } = event;

      const hasOtherStatuses = orderItems?.some(
        (oi) =>
          oi.status !== eventBookingStatus.NOTIFY_ADMIN &&
          oi.status !== eventBookingStatus.CANCEL
      );

      orderItems.forEach(
        ({
          hourlyRate = 0,
          oldHourlyRate = 0,
          tipPrice_,
          extendedPrice_,
          extendedQuantity_ = 0,
          status,
          isDurationIncrease,
          orderItemTypeId,
          quantity,
          price,
          tipPrice,
        }) => {
          if (
            status === eventBookingStatus.CANCEL ||
            (hasOtherStatuses && status === eventBookingStatus.NOTIFY_ADMIN)
          )
            return;

          if (tipPrice_ != null) {
            totals.tipTotal += Number(tipPrice_);
          } else if (extendedPrice_ != null) {
            totals.shutterAndRecapTotal += extendedPrice_ * extendedQuantity_;
          } else if (
            status === eventBookingStatus.EDITED &&
            orderItemTypeId === orderItemType.EXTENDED
          ) {
            totals.shutterAndRecapTotal += quantity * price;
          } else if (
            status === eventBookingStatus.EDITED &&
            orderItemTypeId === orderItemType.TIP
          ) {
            totals.shutterAndRecapTotal += tipPrice;
          } else if (isDurationIncrease) {
            totals.shutterAndRecapTotal += duration * hourlyRate;
          } else {
            totals.shutterAndRecapTotal += duration * oldHourlyRate;
          }
        }
      );

      reels.forEach(({ amount = 0, status }) => {
        if (status === eventBookingStatus.CANCEL) return;
        totals.shutterAndRecapTotal += amount;
      });

      return totals;
    },
    { tipTotal: 0, shutterAndRecapTotal: 0 }
  );
};

// Calculate all event reels total fee
export const calculateAllRecapRateTotalFee = (reels = []) => {
  return (
    reels?.reduce((sum, { amount = 0, status }) => {
      if (status === eventBookingStatus.CANCEL) return sum;
      return sum + amount;
    }, 0) || 0
  );
};

// Formats a number to a fixed number of decimal places, defaults to 2, returns empty string if input is invalid
export const numberToDecimals = (num) => {
  let parsed = Number(num);

  if (isNaN(parsed)) {
    console.error(`Invalid input: "${num}" is not a number.`);
    return EMPTY_STRING;
  }

  // Work entirely in integers (no floats)
  let cents = Math.round(parsed * 100);

  const dollars = (cents / 100).toFixed(2);
  return dollars;
};

// Calculates and returns the percentage value of a given number, handles invalid input
export const calculatePercentage = (num, percent) => {
  const parsedNum = Number(num);
  const parsedPercent = Number(percent);

  if (isNaN(parsedNum) || isNaN(parsedPercent)) {
    return 0;
  }
  const result = (parsedNum * parsedPercent) / 100;

  return result;
};

export const isDateBeforeMinimumBookingDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return true;

  const minBookingDate = getFutureDate(MIN_BOOKING_LEAD_DAYS);
  minBookingDate.setHours(0, 0, 0, 0);

  const selectedDate = new Date(date);
  selectedDate.setHours(0, 0, 0, 0);

  return selectedDate < minBookingDate;
};

export const getDuplicateEventName = (events = []) => {
  const seen = new Set();

  for (const event of events) {
    const name = event.eventName?.trim().toLowerCase();

    if (name && seen.has(name)) {
      return event.eventName;
    }

    seen.add(name);
  }

  return null;
};

export const filterItemsById = (reels, updatedModel, key = "eventId") => {
  const validEventIds = new Set(updatedModel.map((event) => event.id));
  return reels.filter(
    (reelItem) =>
      reelItem[key] === ALL_EVENT.id || validEventIds.has(reelItem[key])
  );
};

// Returns the value if valid; otherwise returns EMPTY_STRING for null, undefined, or invalid strings
export const normalizeValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "null" ||
    value === UNDEFINED ||
    value === "[object Object]"
  ) {
    return EMPTY_STRING;
  }

  return value;
};

export const generateShutterbDetails = (
  shutterB,
  event,
  keysToInclude = null,
  isEditEvent = false,
  isMobileView = false
) => {
  const isTipExist = !!shutterB?.tipPrice_;

  const firstName =
    shutterB?.firstName ?? shutterB?.sbUser?.firstName ?? EMPTY_STRING;
  const lastName =
    shutterB?.lastName ?? shutterB?.sbUser?.lastName ?? EMPTY_STRING;

  const quantity = isTipExist
    ? DEFAULT_VALUE_CART_TABLE
    : (shutterB?.extendedQuantity_ ?? event?.duration);

  const rate = isTipExist
    ? DEFAULT_VALUE_CART_TABLE
    : shutterB?.isDurationIncrease
      ? `$${numberToDecimals(
          shutterB?.hourlyRate || shutterB?.extendedPrice_ || 0
        )}`
      : `$${shutterB?.oldHourlyRate || 0}`;

  const total = !isTipExist
    ? `$${numberToDecimals(
        shutterB?.extendedPrice_
          ? shutterB.extendedQuantity_ * shutterB?.extendedPrice_
          : shutterB?.isDurationIncrease
            ? event.duration * shutterB?.hourlyRate
            : event.duration * shutterB?.oldHourlyRate
      )}`
    : `$${shutterB?.tipPrice_}`;
  const status = shutterB?.status ?? DEFAULT_VALUE_CART_TABLE;
  const allItems = [
    {
      key: SHUTTERB_DETAILS_KEYS.SHUTTERB,
      label: "shutterb:",
      value: truncateString(`${firstName} ${lastName}`, isMobileView ? 11 : 25),
    },
    {
      key: SHUTTERB_DETAILS_KEYS.DATE,
      label: "date:",
      value: event?.eventDate ?? EMPTY_STRING,
    },
    {
      key: SHUTTERB_DETAILS_KEYS.START_TIME,
      label: "start time:",
      value: formatDateIntoTime(event?.sbStartTime),
    },
    {
      key: SHUTTERB_DETAILS_KEYS.DURATION,
      label: "duration:",
      value: `${event?.duration ?? EMPTY_STRING} hours`,
    },
    ...(isEditEvent
      ? [
          {
            key: SHUTTERB_DETAILS_KEYS.OLD_QUANTITY,
            label: "old qty/hrs:",
            value: event?.oldDuration ?? ZERO,
          },
        ]
      : []),
    {
      key: SHUTTERB_DETAILS_KEYS.QUANTITY,
      label: "new qty/hrs:",
      value: quantity,
    },
    { key: SHUTTERB_DETAILS_KEYS.RATE, label: "rate:", value: rate },
    ...(isTipExist
      ? [
          {
            key: SHUTTERB_DETAILS_KEYS.TIP,
            label: "tip:",
            value: `$${shutterB?.tipPrice_ ?? ZERO}`,
          },
        ]
      : []),
    { key: SHUTTERB_DETAILS_KEYS.TOTAL, label: "total:", value: total },
    ...(isEditEvent
      ? [
          {
            key: SHUTTERB_DETAILS_KEYS.STATUS,
            label: "status:",
            value:
              quantity === event?.oldDuration &&
              status === eventBookingStatus.EDITED
                ? CART_SUMMARY_STATUS[eventBookingStatus.NO_CHANGE]?.label
                : CART_SUMMARY_STATUS[status]?.label || status,
            className:
              quantity === event?.oldDuration &&
              status !== eventBookingStatus.EDITED
                ? CART_SUMMARY_STATUS[eventBookingStatus.NO_CHANGE]?.className
                : (CART_SUMMARY_STATUS[status]?.className ?? EMPTY_STRING),
          },
        ]
      : []),
  ];

  return keysToInclude
    ? allItems.filter((item) => keysToInclude.includes(item.key))
    : allItems;
};

export const generateRecapDetails = (
  reelItem,
  recapTypes,
  prefix = EMPTY_STRING,
  isEditEvent = false
) => {
  const status = reelItem?.status ?? DEFAULT_VALUE_CART_TABLE;
  return [
    {
      key: recapDetailsKeys.TYPE,
      label: "type:",
      value: prefix
        ? `${prefix} – ${reelItem.reelMode}`
        : `${reelItem.reelMode}`,
    },
    ...(isEditEvent
      ? [
          {
            key: recapDetailsKeys.OLD_QUANTITY,
            label: "old quantity:",
            value: `${reelItem.oldQuantity}`,
          },
        ]
      : []),
    {
      key: recapDetailsKeys.QUANTITY,
      label: "new quantity",
      value: `${reelItem.quantity}`,
    },
    {
      key: recapDetailsKeys.RATE,
      label: "rate:",
      value: `$${numberToDecimals(reelItem.price)}`,
    },
    {
      key: recapDetailsKeys.TOTAL,
      label: "total:",
      value: `$${numberToDecimals(reelItem.amount)}`,
    },
    ...(isEditEvent
      ? [
          {
            key: recapDetailsKeys.STATUS,
            label: "status:",
            value:
              reelItem.quantity === reelItem.oldQuantity &&
              status === eventBookingStatus.EDITED
                ? CART_SUMMARY_STATUS[eventBookingStatus.NO_CHANGE]?.label
                : CART_SUMMARY_STATUS[status]?.label || status,
            className:
              reelItem.quantity === reelItem.oldQuantity &&
              status === eventBookingStatus.EDITED
                ? CART_SUMMARY_STATUS[eventBookingStatus.NO_CHANGE]?.className
                : (CART_SUMMARY_STATUS[status]?.className ?? EMPTY_STRING),
          },
        ]
      : []),
  ];
};

// Remove a shutterB from a specific event's orderItems
export const removeShutterBFromEvent = (formState, eventId, shutterBId) => {
  return formState.map((event) => {
    if (event.id === eventId) {
      return {
        ...event,
        orderItems: (event.orderItems || [])
          .map((sb) => {
            if (sb.sbUserId === shutterBId) {
              if (sb.status === eventBookingStatus.EDITED) {
                return {
                  ...sb,
                  status: eventBookingStatus.CANCEL, // mark CANCEL instead of removing
                };
              }
              return null; // remove it
            }
            return sb;
          })
          .filter(Boolean),
      };
    }
    return event;
  });
};

export const updateShutterBConflicts = ({
  formState,
  originalFormState,
  availableShutterBs,
  timeMargin,
  oneHourMs,
}) => {
  const updatedFormState = JSON.parse(JSON.stringify(formState));
  const updatedAvailableSBs = JSON.parse(JSON.stringify(availableShutterBs));

  const shutterBConflicts = {}; // shutterBIds with conflicts

  for (let i = 0; i < updatedFormState.length; i++) {
    const baseEvent = updatedFormState[i];
    const removedEvents = (originalFormState || []).filter(
      (origEvent) => !updatedFormState.some((e) => e.id === origEvent.id)
    );

    for (let removedEvent of removedEvents) {
      updatedFormState.push({
        ...removedEvent,
        status: eventBookingStatus.CANCEL,
        orderItems: (removedEvent.orderItems || []).map((item) => ({
          ...item,
          status: eventBookingStatus.CANCEL,
        })),
      });
    }

    // get original event
    const originalEvent = (originalFormState || []).find(
      (e) => e.id === baseEvent.id
    );
    const originalItems = originalEvent?.orderItems || [];

    // Check if previously selected shutterBs still exist in availableShutterBs for this event
    const availableForThisEvent =
      updatedAvailableSBs.find((sb) => sb.eventId === baseEvent.id)?.items ||
      [];

    //Create a lookup set for sbUserIds
    const availableSbUserIds = new Set(
      (availableForThisEvent || []).map((available) => available.sbUserId)
    );

    const stillValidOrderItems = (baseEvent.orderItems || []).filter(
      (item) =>
        item.status === eventBookingStatus.CANCEL ||
        (item.status === eventBookingStatus.NOTIFY_ADMIN &&
          availableSbUserIds.has(item.sbUserId)) || // NEW: keep CANCEL and NOTIFY_ADMIN shutterBs in orderItems
        availableSbUserIds.has(item.sbUserId)
    );

    //detect removed old items which are now not available in current shutterbz list
    const removedItems = originalItems.filter(
      (item) =>
        (item.status === eventBookingStatus.CREATED ||
          item.status === eventBookingStatus.EDITED ||
          item.status === eventBookingStatus.NOTIFY_ADMIN) &&
        !stillValidOrderItems.some((i) => i.sbUserId === item.sbUserId)
    );

    // If none are valid, set empty array and if shutterb marked previously as notify admin
    // and now available in shutterbz list mark it edited againt
    baseEvent.orderItems = [
      ...(stillValidOrderItems.length
        ? stillValidOrderItems.map((item) => ({
            ...item,
            status:
              item.status === eventBookingStatus.NOTIFY_ADMIN &&
              availableSbUserIds.has(item.sbUserId)
                ? eventBookingStatus.EDITED
                : item.status,
          }))
        : []),

      // Append removed items which are coming from db but now not available.
      // Cancel them when replacements are available to select; otherwise keep
      // notify_admin so the whole event can stay notify_admin when none are found.
      ...removedItems.map((item) => ({
        ...item,
        status:
          availableForThisEvent.length > 0
            ? eventBookingStatus.CANCEL
            : eventBookingStatus.NOTIFY_ADMIN,
      })),
    ];

    const { sbStartTime, duration, eventLocation, orderItems = [] } = baseEvent;

    const baseStart = new Date(sbStartTime).getTime();
    const baseEnd = baseStart + duration * oneHourMs;

    for (let sb of orderItems) {
      const shutterBId = sb.sbUserId;

      if (sb.status === eventBookingStatus.CANCEL) {
        continue;
      }

      for (let j = 0; j < updatedFormState.length; j++) {
        if (i === j) continue;

        const compareEvent = updatedFormState[j];
        const compareStart = new Date(compareEvent.sbStartTime).getTime();
        const compareEnd = compareStart + compareEvent.duration * oneHourMs;

        let timeStart, timeEnd;
        if (isLocationsEqual(compareEvent.eventLocation, eventLocation)) {
          timeStart = baseStart;
          timeEnd = baseEnd;
        } else {
          const margin = timeMargin * oneHourMs;
          timeStart = baseStart - margin;
          timeEnd = baseEnd + margin;
        }

        const isOverlap =
          (compareStart >= timeStart && compareStart <= timeEnd) ||
          (compareEnd >= timeStart && compareEnd <= timeEnd);

        if (isOverlap) {
          // Remove from orderItems
          compareEvent.orderItems = (compareEvent.orderItems || []).filter(
            (item) =>
              item.status === eventBookingStatus.CANCEL || // keep CANCEL
              item.sbUserId !== shutterBId
          );
          // Mark conflict for that event
          if (!shutterBConflicts[compareEvent.id]) {
            shutterBConflicts[compareEvent.id] = new Set();
          }
          shutterBConflicts[compareEvent.id].add(shutterBId);
        }
      }
    }
  }

  // Apply removeShutterB flags based on conflicts map
  for (let entry of updatedAvailableSBs) {
    const conflictingIds = shutterBConflicts[entry.eventId] || new Set();
    entry.items = entry.items.map((item) => ({
      ...item,
      removeShutterB: conflictingIds.has(item.sbUserId),
    }));
  }

  return {
    updatedFormState,
    updatedAvailableShutterBs: updatedAvailableSBs,
  };
};

// Converts a date string to Unix epoch seconds
export const toEpochSeconds = (value) => {
  if (!value) return value ?? null;

  if (typeof value === NUMBER) {
    return value;
  }
  const timestamp = Date.parse(value);
  return isNaN(timestamp) ? null : Math.floor(timestamp / ONE_THOUSAND);
};

export const isVideoUrl = (url = "") => {
  const videoExtensions = [
    ".mp4",
    ".webm",
    ".ogg",
    ".avi",
    ".mov",
    ".wmv",
    ".flv",
    ".mkv",
  ];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.endsWith(ext));
};

// Updates a description (AI Response) field in Event Details (Event Booking Flow) (e.g., eventType.description) for a specific event in formState
export const updateEventDetailAIResponse = ({
  formState,
  eventId,
  eventType,
  description,
  value,
}) => {
  return formState.map((event) =>
    event.id === eventId
      ? {
          ...event,
          [eventType]: {
            ...(event[eventType] || {}),
            [description]: value,
          },
        }
      : event
  );
};

export const generateEvents = (
  userId,
  checkboxes,
  isEditDraftEvent = false,
  isSBTipExtendCart = false,
  affiliateCode,
  isEditEvent = false
) => {
  const parsedData = getLocalStorageItem(localStorageKeys.DATA_TO_SEND, {
    Event: [],
  });
  if (!parsedData?.Event?.length) return [];

  return parsedData.Event.map((event) => {
    const hasOtherStatuses = event.orderItems?.some(
      (oi) =>
        oi.status !== eventBookingStatus.NOTIFY_ADMIN &&
        oi.status !== eventBookingStatus.CANCEL
    );

    return {
      ...event,
      SourceTimeZone:
        Intl?.DateTimeFormat()?.resolvedOptions().timeZone || "UTC",
      isNewEvent: isSBTipExtendCart ? false : (event.isNewEvent ?? true),
      eventTypeId: event?.isSaveAsDraft
        ? EVENT_TYPE_ID?.DRAFT
        : event?.eventTypeId || EVENT_TYPE_ID.EVENT,
      eventType:
        event?.isSaveAsDraft || isSBTipExtendCart
          ? {}
          : {
              ...event.eventType,
              id: isEditDraftEvent ? 0 : event.eventType?.id,
            },
      // Replacement shutterB selected — clear event-level notify_admin.
      // When no shutterB is found, hasOtherStatuses is false and notify_admin remains.
      ...(isEditEvent &&
        hasOtherStatuses &&
        event.status === eventBookingStatus.NOTIFY_ADMIN && {
          status: eventBookingStatus.EDITED,
        }),
      ...(isSBTipExtendCart && {
        status: eventBookingStatus.TIP_OR_EXTEND,
      }),
      userId: userId,
      affiliateCode: affiliateCode,
      isActive: IS_ACTIVE.TRUE,
      termsAndConditions: checkboxes?.termsAndConditions ?? false,
      privacyPolicy: checkboxes?.privacyPolicy ?? false,
      isConsentForMarketing: checkboxes?.isConsentForMarketing ?? false,
      isGallerySharedPermission: checkboxes?.isGallerySharedPermission ?? false,

      orderItems: (event.orderItems || []).map(
        ({
          id,
          orderItemTypeId,
          price,
          tipPrice_,
          extendedPrice_,
          extendedQuantity_,
          _type,
          sbUser,
          ...rest
        }) => {
          // used to store the tip/extend price and quantity
          const orderItemConfig = {
            tipPrice: 0,
            price: price ?? rest.hourlyRate,
            quantity: parseFloat(event.duration),
          };

          if (isSBTipExtendCart) {
            orderItemConfig.stripeOrderPaymentId = 0;
            if (rest.status === eventBookingStatus.TIP) {
              orderItemConfig.tipPrice = tipPrice_ ?? 0;
              orderItemConfig.price = 0;
              orderItemConfig.quantity = 0;
            } else if (rest.status === eventBookingStatus.EXTENDED) {
              orderItemConfig.tipPrice = 0;
              orderItemConfig.price = extendedPrice_ ?? 0;
              orderItemConfig.quantity = extendedQuantity_ ?? 0;
            } else {
              orderItemConfig.price = price ?? rest.hourlyRate;
              orderItemConfig.quantity = parseFloat(event.duration);
            }
          }

          if (
            rest.status === eventBookingStatus.EDITED &&
            orderItemTypeId === orderItemType.TIP
          ) {
            orderItemConfig.tipPrice = rest.tipPrice;
            orderItemConfig.totalPrice = rest.totalPrice;
          } else if (
            rest.status === eventBookingStatus.EDITED &&
            orderItemTypeId === orderItemType.EXTENDED
          ) {
            orderItemConfig.price = price;
            orderItemConfig.quantity = rest.quantity;
            orderItemConfig.totalPrice = rest.quantity * price;
          }

          return {
            ...rest,
            ...((isEditDraftEvent ||
              (isEditEvent && rest?.status !== eventBookingStatus.CREATED)) && {
              id,
            }),
            ...(isEditEvent &&
              hasOtherStatuses &&
              // Previous unavailable shutterB was notify_admin; cancel it once a
              // replacement shutterB has been selected. If no replacement exists,
              // hasOtherStatuses is false and notify_admin is left unchanged.
              rest?.status === eventBookingStatus.NOTIFY_ADMIN && {
                status: eventBookingStatus.CANCEL,
              }),
            orderItemTypeId:
              orderItemTypeId ||
              eventBookingConstants.ORDER_ITEM_TYPE_ID.NEW_EVENT_SB,
            eventOrderItemMap:
              isArrayWithItems(rest.eventOrderItemMap) &&
              rest.eventOrderItemMap.length > 0
                ? rest.eventOrderItemMap
                : [
                    {
                      eventId: event.id,
                      orderItemId: id,
                      isActive: true || null,
                    },
                  ],
            ...orderItemConfig,
          };
        }
      ),
    };
  });
};

export const checkShutterBSelectionValid = (
  formState,
  availableShutterBs,
  isDraftEvent,
  isEditEvent,
  eventBookingStatus
) => {
  if (!Array.isArray(formState) || formState.length === 0) return null;

  for (const event of formState) {
    const matchedAvailableForNotify = availableShutterBs.find(
      (item) => item.eventId === event.id
    );

    const hasValidNotifyAdminSelection =
      Array.isArray(event.orderItems) &&
      event.orderItems.some((orderItem) => {
        return (
          orderItem.status === eventBookingStatus.NOTIFY_ADMIN &&
          orderItem.sbUserId &&
          matchedAvailableForNotify?.items?.some(
            (sb) => sb.sbUserId === orderItem.sbUserId
          )
        );
      });

    //If condition matches -> skip validation for this event
    if (hasValidNotifyAdminSelection) {
      continue;
    }

    if (isDraftEvent) {
      // For draft event, ensure at least one shutterB is selected (isSelected: true)
      const hasSelected =
        Array.isArray(event.orderItems) &&
        event.orderItems.some((item) => item.isSelected === true);

      if (!hasSelected) return event.id; // return first invalid event id
    } else {
      const matchedAvailable = availableShutterBs.find(
        (item) => item.eventId === event.id
      );

      const selectableShutterBs =
        matchedAvailable?.items?.filter((sb) => !sb.removeShutterB) || [];

      if (selectableShutterBs.length > 0) {
        // shutterBs available, ensure at least one shutterb is selected
        const hasSelected =
          Array.isArray(event.orderItems) && event.orderItems.length > 0;
        if (!hasSelected) return event.id; // return first invalid event id
      } else {
        if (!isEditEvent) {
          // No shutterBs available, check isSaveAsDraft flag
          if (!event.isSaveAsDraft) return event.id;
        }
      }
    }
  }

  return null; // all valid
};

// Removes all events marked as draft (isSaveAsDraft: true) from the formState
export const removeDraftEvents = (formState = []) => {
  return Array.isArray(formState)
    ? formState.filter((event) => !event.isSaveAsDraft)
    : [];
};

// Check if all events are draft
export function areAllEventsIsDraft(formState) {
  if (!Array.isArray(formState) || formState.length === 0) return false;
  return formState.every((event) => event.isSaveAsDraft === true);
}

// Event Details tab validation (Event Booking Form)
export const isEventDetailsValid = (eventType) => {
  if (!eventType) return false;

  const name = eventType.name?.trim();
  const description = eventType.description;
  return !(!name || name === "" || isHtmlContentEmpty(description));
};

export const isLocationsEqual = (loc1, loc2) => {
  if (!loc1 || !loc2) return false;

  return loc1.latitude === loc2.latitude && loc1.longitude === loc2.longitude;
};

// Scroll to the top of the screen
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // change to "auto" for instant jump
  });
};

// Instant jump to the top of the screen
export const scrollWindowToTop = () => {
  window.scrollTo(0, 0);
};

export const formatGalleryItemData = (file) => {
  const baseUrl = process.env.REACT_APP_S3_BASE_URL_EVENTS_GALLERY;

  const imageUrl = `${baseUrl}/${file.imageUrl}`;
  const thumbnailUrl = `${baseUrl}/${file.thumbnailUrl}`;
  const name = file.imageUrl?.slice(0, -4);

  let fileType = MEDIA_MIME_TYPES.JPEG;
  let galleryType = galleryConstants.GALLERY_IMAGES;

  if (VIDEO_EXTENSIONS.MP4.some((ext) => file.imageUrl?.endsWith(ext))) {
    fileType = MEDIA_MIME_TYPES.MP4;
    galleryType = galleryConstants.GALLERY_VIDEOS;
  } else if (VIDEO_EXTENSIONS.MOV.some((ext) => file.imageUrl?.endsWith(ext))) {
    fileType = MEDIA_MIME_TYPES.MOV;
    galleryType = galleryConstants.GALLERY_VIDEOS;
  } else if (VIDEO_EXTENSIONS.M4V.some((ext) => file.imageUrl?.endsWith(ext))) {
    fileType = MEDIA_MIME_TYPES.M4V;
    galleryType = galleryConstants.GALLERY_VIDEOS;
  }

  return {
    file: new File([file], name, {
      type: fileType,
      lastModified: file.lastModified,
      lastModifiedDate: file.lastModifiedDate,
    }),
    url: imageUrl,
    thumbnailUrl: thumbnailUrl,
    selected: false,
    id: file?.id,
    isLocked: file?.isLocked,
    isFavorite: file?.isFavorite,
    contentType: file?.contentType,
    galleryType:
      file?.contentType === galleryConstants.CONTENT_TYPE_REELS
        ? galleryConstants.GALLERY_REELS
        : galleryType,
  };
};

// Recap Feedback Form Validation
export const validateRecapFeedbackForm = (formData) => {
  const updatedForm = formData.map((item) => {
    const errors = {};
    const isNewData = item.actionType === RECAP_FEEDBACK_ACTION.SIMPLE_FEEDBACK;

    if (!item.actionType && item.actionType !== 0) {
      errors.actionType = messages.Action_Type_Is_Required;
    }

    // New data validation (SIMPLE_FEEDBACK only)
    if (isNewData) {
      if (!item.feedback) {
        errors.feedback = messages.FEEDBACK_Is_Required;
      }
    } else {
      // Old data validation (for existing feedback)
      if (!item.timeRequested) {
        errors.timeRequested = messages.Time_Is_Required;
      }

      const isDeleteAction = item.actionType === RECAP_FEEDBACK_ACTION.DELETE;
      const isSongFeedbakcAction =
        item.actionType === RECAP_FEEDBACK_ACTION.SONG_FEEDBACK;

      if (!(isDeleteAction || isSongFeedbakcAction) && !item.filename) {
        errors.filename = messages.File_Name_Is_Required;
      }

      if (isSongFeedbakcAction && !item.feedback) {
        errors.feedback = messages.FEEDBACK_Is_Required;
      }
    }

    return {
      ...item,
      errors,
    };
  });

  const hasErrors = updatedForm.some(
    (item) => Object.keys(item.errors).length > 0
  );
  return { updatedForm, isValid: !hasErrors };
};

export const errorHandler = (error) => {
  let message = "An unexpected error occurred";

  if (!error) {
    message = "Unknown error";
  } else if (typeof error === "string") {
    // Plain string error
    message = error;
  } else if (Array.isArray(error)) {
    // Array of errors (take first)
    message = error.length > 0 ? error[0] : "Unknown error";
  } else if (error instanceof Error) {
    // JS Error object
    message = error.message || "Unknown error";
  } else if (typeof error === "object") {
    // API error response objects
    if (error.message) {
      message = error.message;
    } else if (error.error) {
      message = error.error;
    } else if (error.errors && Array.isArray(error.errors)) {
      message = error.errors[0] || "Unknown error";
    } else {
      message = JSON.stringify(error); // fallback to stringified object
    }
  }

  customToast(i18next.t(message ?? "undefined"), toast.TYPE.ERROR);
};

// Returns S3 URL by appending the relative path to the base gallery URL from environment
export const getS3GalleryUrl = (relativeUrl) => {
  if (!relativeUrl) return EMPTY_STRING;
  return `${process.env.REACT_APP_S3_BASE_URL_EVENTS_GALLERY}/${relativeUrl}`;
};

// Deep clone the data
export const deepClone = (data) => {
  return JSON.parse(JSON.stringify(data));
};

// Returns true if the given value is a non-empty array
export const isArrayWithItems = (arr) => {
  return Array.isArray(arr) && arr.length > 0;
};

// Determine whether the cart should be preserved by comparing orderId
// For Tip and Extend actions:
//   - return true if the orderId matches
//   - return false if it belongs to a different event group
export const isKeepCustomerDashboardCart = (customerDashboardCart, orderId) => {
  return (
    !Array.isArray(customerDashboardCart) ||
    customerDashboardCart.length === 0 ||
    customerDashboardCart[0]?.orderId === orderId
  );
};

export const convertEpochToTimezoneMillis = (epochSeconds, timeZone) => {
  if (!epochSeconds || !timeZone) return epochSeconds;

  const utcDate = new Date(epochSeconds * ONE_THOUSAND);
  const tzString = utcDate.toLocaleString("en-US", { timeZone });
  const tzDate = new Date(tzString);
  return tzDate / ONE_THOUSAND;
};

// Utility to generate a short unique ID (8 characters)
// Combines the current timestamp (in base-36) with a random string
// Then trims the result to a fixed length of 8 characters
export const generateUniqueId = () => {
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
  ).slice(0, 8);
};

export const capitalizeWords = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const safeJsonParse = (response, fallback = []) => {
  if (!response || typeof response !== "string") return fallback;

  try {
    return JSON.parse(response);
  } catch (err) {
    try {
      // Try auto-clean
      let clean = response.trim();

      // Remove junk before JSON
      clean = clean.replace(/^[^([{]+/, "");

      // Remove junk after JSON
      clean = clean.replace(/[^)\]}]+$/, "");

      // Remove newlines
      clean = clean.replace(/\r?\n|\r/g, "");

      // Replace smart quotes
      clean = clean.replace(/“|”/g, '"');

      // Remove trailing commas (invalid JSON)
      clean = clean.replace(/,(\s*[}\]])/g, "$1");

      return JSON.parse(clean);
    } catch (cleanupErr) {
      return fallback;
    }
  }
};

export const scrollToFirstError = (errors) => {
  if (errors && errors.length > 0) {
    const firstErrorField = document.querySelector(`[name="${errors[0]}"]`);
    if (firstErrorField) {
      firstErrorField.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      firstErrorField.focus({ preventScroll: true });
    }
  }
};
